import React, {useState, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Divider} from '@salesforce/retail-react-app/app/components/shared/ui'

export const RichTextBox = ({
    richText,
    contentPosition,
    color,
    bgcolor,
    fontWeight,
    textSizeDefault,
    textSizeTablet,
    textSizeDesktop,
    readMore,
    readMoreText,
    readLessText,
    haveAQuestionText
}) => {
    const [expanded, setExpanded] = useState(false)
    const [shouldTruncate, setShouldTruncate] = useState(false)
    const [isHydrated, setIsHydrated] = useState(false)
    const contentRef = useRef(null)

    // Pre-sanitize text on both server and client
    const sanitizeText = (html) => {
        return html?.replace(/^<p>(.*)<\/p>$/s, '$1') || ''
    }

    const text = sanitizeText(richText?.value)
    const sanitizedQuestionText = sanitizeText(haveAQuestionText)
    const textColor = color?.value
    const bckcolor = bgcolor?.value

    // Ensure we're on the client side
    useEffect(() => {
        setIsHydrated(true)
    }, [])

    useEffect(() => {
        if (readMore && contentRef.current && isHydrated) {
            const lineHeight = parseFloat(getComputedStyle(contentRef.current).lineHeight)
            const maxHeight = lineHeight * 3
            if (contentRef.current.scrollHeight > maxHeight) {
                setShouldTruncate(true)
            }
        }
    }, [text, readMore, isHydrated])

    const truncationStyles = isHydrated
        ? {
              overflow: 'hidden',
              display: '-webkit-box',
              lineHeight: 6,
              fontWeight: 'medium',
              WebkitLineClamp: expanded ? 'unset' : 3,
              WebkitBoxOrient: 'vertical'
          }
        : {
              lineHeight: 6,
              fontWeight: 'medium'
          }

    return (
        <Box
            textAlign={contentPosition}
            color={textColor}
            bgColor={bckcolor}
            fontWeight={fontWeight}
            fontSize={{base: textSizeDefault, md: textSizeTablet, lg: textSizeDesktop}}
            py={{base: '2', lg: '32px'}}
            px={{
                base: '4',
                sm: '8',
                lg: '16'
            }}
            maxWidth={'1920px'}
            margin={'auto'}
        >
            {readMore ? (
                <Box
                    ref={contentRef}
                    dangerouslySetInnerHTML={{__html: text}}
                    sx={truncationStyles}
                    suppressHydrationWarning
                />
            ) : (
                <Box
                    ref={contentRef}
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{__html: text}}
                />
            )}

            {readMore && shouldTruncate && isHydrated && (
                <Button
                    variant="link"
                    onClick={() => setExpanded(!expanded)}
                    marginTop="16px"
                    lineHeight={'6'}
                    sx={{
                        color: 'black',
                        textDecoration: 'underline',
                        fontWeight: '800',
                        _hover: {color: 'black', textDecoration: 'underline'}
                    }}
                >
                    {expanded ? readLessText || 'Read Less' : readMoreText || 'Read More'}

                    {/* Live region for screen reader announcement */}
                    <Box
                        as="span"
                        role="status"
                        aria-live="polite"
                        sx={{position: 'absolute', left: '-9999px'}} // visually hidden
                    >
                        {expanded ? readLessText || 'Read Less' : readMoreText || 'Read More'}
                    </Box>
                </Button>
            )}

            {haveAQuestionText && (
                <Box>
                    <Divider
                        my={6}
                        bg="radial-gradient(1500% 50% at 50% 50%,rgba(74,78,88,.11) 100%,rgba(35,37,43,.35) 0)"
                        border={'none'}
                        h={'1px'}
                    />
                    <Box
                        pb={6}
                        fontSize={'md'}
                        fontWeight={'extrabold'}
                        lineHeight={6}
                        sx={{
                            a: {
                                color: '#23252b',
                                textDecoration: 'underline',
                                textDecorationThickness: '1px',
                                textUnderlineOffset: '4px',
                                cursor: 'pointer'
                            }
                        }}
                        dangerouslySetInnerHTML={{__html: sanitizedQuestionText}}
                    />
                </Box>
            )}
        </Box>
    )
}

RichTextBox.propTypes = {
    richText: PropTypes.object,
    contentPosition: PropTypes.string,
    color: PropTypes.object,
    bgcolor: PropTypes.object,
    fontWeight: PropTypes.string,
    textSizeDefault: PropTypes.number,
    textSizeDesktop: PropTypes.number,
    textSizeTablet: PropTypes.number,
    readMore: PropTypes.bool,
    readMoreText: PropTypes.string,
    readLessText: PropTypes.string,
    haveAQuestionText: PropTypes.string
}

export default RichTextBox
